import { getFullUrl } from "@/lib/get-full-url";
import type { DocSummary, PostSummary } from "@webapp/utils/content.server";
import { VIDEOS, youtubeThumbnailUrl, youtubeWatchUrl } from "@webapp/utils/videos";

// schema.org JSON-LD, read by search engines for rich results (video thumbnails,
// breadcrumbs, dates). Returned as React Router meta descriptors.

const PUBLISHER = {
  "@type": "Organization",
  name: "HypeStream",
  url: getFullUrl("/"),
  logo: { "@type": "ImageObject", url: getFullUrl("/hype-stream.png") },
};

function breadcrumbs(items: { name: string; path: string }[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: getFullUrl(item.path),
    })),
  };
}

export function blogPostStructuredData(post: PostSummary) {
  const { title, description, date, updated, video: videoKey } = post.frontmatter;
  const video = VIDEOS[videoKey];
  const url = getFullUrl(`/blog/${post.slug}`);
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: title,
          description,
          url,
          mainEntityOfPage: url,
          image: youtubeThumbnailUrl(videoKey),
          datePublished: date,
          dateModified: updated ?? date,
          author: PUBLISHER,
          publisher: PUBLISHER,
          video: {
            "@type": "VideoObject",
            name: video.title,
            description,
            thumbnailUrl: youtubeThumbnailUrl(videoKey),
            // Posts go up the day their video does.
            uploadDate: date,
            embedUrl: `https://www.youtube.com/embed/${video.youtubeId}`,
            url: youtubeWatchUrl(videoKey),
          },
        },
        breadcrumbs([
          { name: "Blog", path: "/blog" },
          { name: title, path: `/blog/${post.slug}` },
        ]),
      ],
    },
  };
}

export function docStructuredData(doc: DocSummary) {
  const { title, description, updated } = doc.frontmatter;
  const url = getFullUrl(`/docs/${doc.slug}`);
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TechArticle",
          headline: title,
          description,
          url,
          mainEntityOfPage: url,
          dateModified: updated,
          author: PUBLISHER,
          publisher: PUBLISHER,
        },
        breadcrumbs([
          { name: "Docs", path: "/docs" },
          { name: title, path: `/docs/${doc.slug}` },
        ]),
      ],
    },
  };
}
